import type { ReactNode } from 'react';
import { useCallback } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native';

import type {
  AlertLabels,
  AlertProps
} from '@/features/popup/components/Alert';
import { usePopup } from '@/features/popup/provider/usePopup';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

// Rich confirmation bodies bring their own layout, so hide the default
// top/bottom divider lines of Dialog.ScrollArea.
const styles = StyleSheet.create({
  borderlessScrollArea: {
    borderTopWidth: 0,
    borderBottomWidth: 0
  }
});

export type ConfirmationDialogOptions = {
  /**
   * Labels are provided by the caller (translated in the caller's namespace,
   * typically via `t('...', { returnObjects: true })`). `title` is required,
   * `confirm`/`cancel` default to a simple confirm/cancel pair when omitted by
   * the caller's labels.
   */
  labels: AlertLabels;
  /**
   * Optional rich body rendered inside the dialog's scroll area instead of a
   * plain `labels.content` text (e.g. a sender / visibility summary).
   */
  content?: ReactNode;
  /**
   * Style override for the scroll area wrapping `content`. Defaults to a
   * borderless area (no top/bottom divider lines).
   */
  scrollAreaStyle?: StyleProp<ViewStyle>;
  /**
   * Marks the confirm action as destructive (confirm button in error color).
   * Use for irreversible actions such as deletions.
   */
  destructive?: boolean;
  /** Called when the user confirms the dialog. */
  onConfirm: () => void;
  /** Called when the user cancels the dialog. */
  onCancel?: () => void;
};

/**
 * Reusable confirmation dialog built on top of the global `usePopup().showAlert`.
 *
 * Use this for any "are you sure?" confirmation (publish, delete, overwrite, …)
 * instead of wiring `showAlert` by hand. The component stays label-driven: the
 * caller supplies already-translated labels, so the dialog itself needs no
 * localization namespace.
 *
 * Example:
 * ```ts
 * const showConfirmation = useConfirmationDialog();
 * showConfirmation({
 *   labels: t('alerts.confirm-publish', { returnObjects: true }),
 *   onConfirm: () => publish()
 * });
 * ```
 */
export const useConfirmationDialog = () => {
  const { showAlert } = usePopup();
  const theme = useAppTheme();

  return useCallback(
    ({
      labels,
      content,
      scrollAreaStyle = styles.borderlessScrollArea,
      destructive = false,
      onConfirm,
      onCancel
    }: ConfirmationDialogOptions) =>
      showAlert({
        dismissable: true,
        labels,
        confirmColor: destructive ? theme.colors.error : undefined,
        scrollAreaStyle,
        onConfirm,
        onCancel,
        // `children` is not part of AlertProps' Omit-typed showAlert signature,
        // but Alert renders it as the dialog body.
        ...({ children: content } as Partial<AlertProps>)
      }),
    [showAlert, theme.colors.error]
  );
};
